import React, { forwardRef } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Avatar as PrimitiveAvatar,
  AvatarImage,
  AvatarFallback,
  AvatarBadge,
  AvatarGroup,
  AvatarGroupCount,
} from "src/primitives/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/primitives/Tooltip";

import { STATUS_COLOR_MAP, SIDE_MAP, XL_CLASSES } from "./constants";
import { getAvatarColorStyle, getInitials } from "./utils";

type AvatarSize = "sm" | "default" | "lg" | "xl";

interface AvatarTooltipProps {
  /** Tooltip content. Defaults to user.name if showTooltip is true. */
  content?: React.ReactNode;
  /** Tooltip position. */
  position?: string;
}

export interface AvatarProps extends Omit<
  React.ComponentProps<typeof PrimitiveAvatar>,
  "size"
> {
  /** User data for the avatar. */
  user?: {
    id?: string | number;
    name?: string;
    email?: string;
    imageUrl?: string;
  };
  /** Status indicator displayed as a colored badge. */
  status?: "online" | "idle" | "offline" | null;
  /** Show the user's name in a tooltip on hover. */
  showTooltip?: boolean;
  /** Tooltip configuration. Overrides showTooltip content. */
  tooltipProps?: AvatarTooltipProps;
  /** Size of the avatar. */
  size?: AvatarSize;
  /** Additional CSS class names. */
  className?: string;
}

const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      user,
      status,
      showTooltip = false,
      tooltipProps,
      size = "default",
      className,
      ...otherProps
    },
    ref
  ) => {
    const initials = getInitials(user?.name);
    const isXl = size === "xl";
    const primitiveSize = isXl ? "lg" : size;
    const seed =
      user?.id != null
        ? String(user.id)
        : user?.email?.trim().toLowerCase() || user?.name;

    const avatarElement = (
      <PrimitiveAvatar
        ref={ref}
        size={primitiveSize}
        className={cn(isXl && XL_CLASSES, className)}
        {...otherProps}
      >
        {user?.imageUrl && (
          <AvatarImage src={user.imageUrl} alt={user?.name ?? ""} />
        )}
        <AvatarFallback style={getAvatarColorStyle(seed)}>
          {initials}
        </AvatarFallback>
        {status && <AvatarBadge className={cn(STATUS_COLOR_MAP[status])} />}
      </PrimitiveAvatar>
    );

    const tooltipContent =
      tooltipProps?.content ?? (showTooltip ? user?.name : null);

    if (tooltipContent) {
      const side = SIDE_MAP[tooltipProps?.position ?? "auto"] ?? "bottom";

      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">{avatarElement}</span>
            </TooltipTrigger>
            <TooltipContent side={side}>{tooltipContent}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return avatarElement;
  }
);

Avatar.displayName = "Avatar";

const AvatarNamespace = Object.assign(Avatar, {
  Group: AvatarGroup,
  GroupCount: AvatarGroupCount,
});

export { AvatarNamespace as Avatar };
